// client/src/components/social/FollowButton.js
import React, { useState, useEffect } from 'react';
import { Button, CircularProgress } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PeopleIcon from '@mui/icons-material/People';
import axiosInstance from '../../axiosInstance';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

/**
 * State-aware follow/unfollow button.
 *
 * Props:
 *   targetUserId  — the user to follow/unfollow
 *   currentUserId — the logged-in user's id (pass null if not logged in)
 *   onStatusChange — optional callback(newStatus) after follow state changes
 *   size          — MUI button size ('small' | 'medium' | 'large'), default 'small'
 *   variant       — MUI button variant, default 'outlined'
 */
export default function FollowButton({
  targetUserId,
  currentUserId,
  onStatusChange,
  size = 'small',
  variant = 'outlined'
}) {
  const { isFeatureEnabled } = useFeatureFlags();
  const [status, setStatus] = useState('none'); // 'none' | 'following' | 'follower' | 'mutual'
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const isEnabled = isFeatureEnabled('enableFollowSystem');

  useEffect(() => {
    if (!isEnabled || !currentUserId || !targetUserId) {
      setLoading(false);
      return;
    }
    if (String(currentUserId) === String(targetUserId)) {
      setLoading(false);
      return;
    }

    axiosInstance.get(`/follow/status/${targetUserId}`)
      .then(res => setStatus(res.data.status))
      .catch(() => setStatus('none'))
      .finally(() => setLoading(false));
  }, [targetUserId, currentUserId, isEnabled]);

  if (!isEnabled || !currentUserId || String(currentUserId) === String(targetUserId)) {
    return null;
  }

  const isFollowing = status === 'following' || status === 'mutual';

  const handleClick = async () => {
    setActing(true);
    try {
      if (isFollowing) {
        await axiosInstance.delete(`/follow/${targetUserId}`);
        const newStatus = status === 'mutual' ? 'follower' : 'none';
        setStatus(newStatus);
        onStatusChange?.(newStatus);
      } else {
        await axiosInstance.post(`/follow/${targetUserId}`);
        const newStatus = status === 'follower' ? 'mutual' : 'following';
        setStatus(newStatus);
        onStatusChange?.(newStatus);
      }
    } catch (err) {
      console.error('Follow action failed:', err);
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return <CircularProgress size={16} sx={{ ml: 1 }} />;
  }

  const label = isFollowing ? (status === 'mutual' ? 'Mutual' : 'Following') : 'Follow';
  const icon = isFollowing
    ? (status === 'mutual' ? <PeopleIcon fontSize="small" /> : <PersonRemoveIcon fontSize="small" />)
    : <PersonAddIcon fontSize="small" />;

  return (
    <Button
      size={size}
      variant={isFollowing ? 'contained' : variant}
      color={isFollowing ? 'primary' : 'inherit'}
      startIcon={acting ? <CircularProgress size={14} color="inherit" /> : icon}
      onClick={handleClick}
      disabled={acting}
    >
      {label}
    </Button>
  );
}
